export const VCB_SENSITIVITY_LEVELS = [
  'public_internal',
  'strategy_sensitive',
  'finance_sensitive',
  'people_sensitive',
  'highly_sensitive',
  'restricted',
] as const;

export type VcbSensitivityLevelDto = (typeof VCB_SENSITIVITY_LEVELS)[number];

export type VcbPayloadRedactionStatusDto = 'none' | 'partial' | 'full' | 'blocked';

export type VcbRedactionReasonCodeDto =
  | 'not_sensitive'
  | 'permission_denied'
  | 'people_data_restricted'
  | 'finance_data_restricted'
  | 'strategy_data_restricted'
  | 'highly_sensitive_restricted'
  | 'policy_blocked'
  | 'redaction_engine_unavailable'
  | 'unknown';

export type VcbPayloadRedactionDto = {
  status: VcbPayloadRedactionStatusDto;
  sensitivity: VcbSensitivityLevelDto;
  policyVersion: string;
  reasonCode: VcbRedactionReasonCodeDto;
  reason: string;
  redactedFields: string[];
  restrictedEvidenceRefs: string[];
  preservesGovernanceMeaning: boolean;
  confidenceImpact: 'none' | 'lowers_confidence' | 'blocks_recommendation' | 'unknown';
};

export type VcbPermissionPostureDto = {
  canViewClaim: boolean;
  canViewEvidence: boolean;
  canViewOverrideRationale: boolean;
  redaction: VcbPayloadRedactionDto;
};

export const VCB_REDACTION_ENGINE_PROVIDERS = [
  'deterministic',
  'self_hosted_openai_privacy_filter',
  'custom_model',
  'hybrid',
] as const;

export type VcbRedactionEngineProviderDto = (typeof VCB_REDACTION_ENGINE_PROVIDERS)[number];

export type VcbRedactionDeploymentModeDto =
  | 'deterministic_only'
  | 'self_hosted'
  | 'managed_private';

export type VcbRedactionUnavailableBehaviorDto = 'fail_closed' | 'block_egress' | 'manual_review';

export type VcbRedactionLabelDto =
  | 'account_number'
  | 'private_address'
  | 'private_date'
  | 'private_email'
  | 'private_person'
  | 'private_phone'
  | 'private_url'
  | 'secret'
  | 'payment_pan'
  | 'payment_cvv'
  | 'payment_token'
  | 'employee_id'
  | 'compensation_adjacent'
  | 'reorg_sensitive'
  | 'strategy_sensitive';

export type VcbRedactionActionDto = 'allow' | 'mask' | 'redact' | 'hash' | 'block';

export type VcbRedactionSpanDto = {
  spanId: string;
  label: VcbRedactionLabelDto | string;
  startOffset: number;
  endOffset: number;
  sourceHash: string;
  confidence: number | null;
  modelVersion: string | null;
  policyVersion: string;
  action: VcbRedactionActionDto;
  sensitivity: VcbSensitivityLevelDto;
  rawTextStored: boolean;
};

export type VcbDeterministicDetectorKindDto =
  | 'payment_pan'
  | 'payment_cvv'
  | 'payment_token'
  | 'secret'
  | 'access_token'
  | 'employee_id'
  | 'compensation_adjacent'
  | 'reorg_sensitive'
  | 'domain_identifier';

export type VcbDeterministicDetectorDto = {
  detectorId: string;
  kind: VcbDeterministicDetectorKindDto;
  enabled: boolean;
  requiresLuhnValidation?: boolean;
  action: VcbRedactionActionDto;
};

export type VcbPciCvvHandlingDto = 'block' | 'redact' | 'hash';

export type VcbPaymentTokenHandlingDto = 'allow_token_only' | 'redact' | 'block';

export type VcbPciDetectionPolicyDto = {
  detectPan: boolean;
  requireLuhnValidation: boolean;
  cvvHandling: VcbPciCvvHandlingDto;
  paymentTokenHandling: VcbPaymentTokenHandlingDto;
  failClosedEgress: boolean;
  denyContextPackOnMatch: boolean;
  denyExportOnMatch: boolean;
};

export type VcbRedactionEgressPathDto = 'context_pack' | 'llm_prompt' | 'export' | 'download';

export type VcbRedactionPolicyDto = {
  policyId: string;
  version: string;
  failClosed: boolean;
  egressPaths: VcbRedactionEgressPathDto[];
  deterministicDetectors: VcbDeterministicDetectorDto[];
  pci: VcbPciDetectionPolicyDto;
};

export type VcbRedactionEvalCorpusKindDto =
  | 'hr'
  | 'finance'
  | 'reorg'
  | 'strategy'
  | 'executive_override'
  | 'pci'
  | 'secrets';

export type VcbRedactionEvalProfileStatusDto =
  | 'not_run'
  | 'required_before_governed_use'
  | 'passing'
  | 'failing';

export type VcbRedactionEvalProfileDto = {
  profileId: string;
  status: VcbRedactionEvalProfileStatusDto;
  corpusKinds: VcbRedactionEvalCorpusKindDto[];
  minRecall: number;
  maxFalsePositiveRate: number;
  lastEvalAt: string | null;
};

export type VcbRedactionEngineDto = {
  engineId: string;
  provider: VcbRedactionEngineProviderDto;
  deploymentMode: VcbRedactionDeploymentModeDto;
  publicHostedDemoAllowed: false;
  modelVersion: string | null;
  policyVersion: string;
  supportedLabels: Array<VcbRedactionLabelDto | string>;
  unavailableBehavior: VcbRedactionUnavailableBehaviorDto;
  evalProfile: VcbRedactionEvalProfileDto;
  deterministicDetectors: VcbDeterministicDetectorDto[];
  pci: VcbPciDetectionPolicyDto;
};
